import clsx from 'clsx';
import { useRef, useState } from 'react';
import { auditionSlice } from '../lib/audition';
import type { Pattern, SourceMeta, Step } from '../types';

interface Props {
  pattern: Pattern;
  source: SourceMeta | null;
  color: string;
  currentStep: number;
  selected: string[];
  beatsPerBar: number;
  onSelect: (ids: string[]) => void;
  onMove: (from: number, to: number) => void;
  onStepChange: (stepId: string, patch: Partial<Step>) => void;
}

/**
 * The step grid. Drag a cell onto another to reorder; alt-drag copies the
 * slice number instead of moving the cell, which is how you build variations
 * fast. The grid is deliberately slice-indexed, so the same pattern can drive
 * any loop you point at it.
 */
export function PatternGrid({
  pattern,
  source,
  color,
  currentStep,
  selected,
  beatsPerBar,
  onSelect,
  onMove,
  onStepChange,
}: Props) {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const altCopy = useRef(false);
  const stepsPerBar = pattern.division * beatsPerBar;

  const indexAt = (x: number, y: number): number | null => {
    const el = document.elementFromPoint(x, y)?.closest('[data-step-index]');
    if (!el) return null;
    const v = Number((el as HTMLElement).dataset.stepIndex);
    return Number.isFinite(v) ? v : null;
  };

  const handleUp = () => {
    if (dragFrom !== null && dragOver !== null && dragFrom !== dragOver) {
      if (altCopy.current) {
        const src = pattern.steps[dragFrom];
        const dst = pattern.steps[dragOver];
        if (src && dst) onStepChange(dst.id, { slice: src.slice, reverse: src.reverse });
      } else {
        onMove(dragFrom, dragOver);
      }
    }
    setDragFrom(null);
    setDragOver(null);
  };

  return (
    <div
      data-tour="pattern-grid"
      className="grid gap-1 select-none"
      style={{ gridTemplateColumns: `repeat(${Math.min(16, stepsPerBar)}, minmax(0,1fr))` }}
      onPointerUp={handleUp}
      onPointerLeave={() => {
        setDragFrom(null);
        setDragOver(null);
      }}
    >
      {pattern.steps.map((step, i) => {
        const isSel = selected.includes(step.id);
        const playing = currentStep === i;
        const rest = step.slice < 0;
        const beatStart = i % pattern.division === 0;
        const barStart = i % stepsPerBar === 0;
        return (
          <button
            key={step.id}
            data-step-index={i}
            onPointerDown={(e) => {
              altCopy.current = e.altKey;
              setDragFrom(i);
              setDragOver(i);
              if (e.shiftKey && selected.length) {
                const anchor = pattern.steps.findIndex((s) => s.id === selected[0]);
                const [a, b] = anchor < i ? [anchor, i] : [i, anchor];
                onSelect(pattern.steps.slice(a, b + 1).map((s) => s.id));
              } else if (e.metaKey || e.ctrlKey) {
                onSelect(
                  isSel ? selected.filter((id) => id !== step.id) : [...selected, step.id],
                );
              } else {
                onSelect([step.id]);
                if (source && !rest) auditionSlice(source, step.slice, step);
              }
            }}
            onPointerMove={(e) => {
              if (dragFrom === null) return;
              const idx = indexAt(e.clientX, e.clientY);
              if (idx !== null) setDragOver(idx);
            }}
            onDoubleClick={() => onStepChange(step.id, { mute: !step.mute })}
            onContextMenu={(e) => {
              e.preventDefault();
              onStepChange(step.id, { reverse: !step.reverse });
            }}
            className={clsx(
              'relative h-14 rounded-md border text-left transition-all duration-75',
              barStart && 'ml-0',
              rest
                ? 'border-dashed border-ink-600 bg-ink-900/60'
                : 'border-ink-600 bg-ink-800 hover:border-ink-400',
              isSel && 'ring-2 ring-volt-500 ring-offset-1 ring-offset-ink-950',
              playing && 'hit',
              step.mute && 'opacity-35',
              dragOver === i && dragFrom !== null && dragFrom !== i && 'border-volt-400 bg-volt-500/20',
              dragFrom === i && 'scale-95 opacity-70',
            )}
            style={
              !rest
                ? {
                    background: playing
                      ? `linear-gradient(180deg, ${color}55, ${color}22)`
                      : `linear-gradient(180deg, ${color}22, transparent)`,
                    borderColor: playing ? color : undefined,
                    boxShadow: playing ? `0 0 12px ${color}66` : undefined,
                  }
                : undefined
            }
          >
            <span className="mono absolute left-1 top-0.5 text-[9px] text-ink-500">
              {beatStart ? (barStart ? `${Math.floor(i / stepsPerBar) + 1}.1` : (i / pattern.division) % beatsPerBar + 1) : ''}
            </span>
            <span
              className="mono absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-sm font-semibold"
              style={{ color: rest ? '#3c4753' : '#e6ebef' }}
            >
              {rest ? '·' : step.slice + 1}
            </span>
            <span className="mono absolute bottom-1.5 left-1 right-1 flex flex-wrap gap-x-1 overflow-hidden text-[8px] leading-none text-ink-300">
              {step.reverse && <span title="Reversed">◀</span>}
              {step.repeat > 1 && <span title="Retrigger">×{step.repeat}</span>}
              {step.probability < 1 && <span title="Probability">{Math.round(step.probability * 100)}%</span>}
              {step.pitch !== 0 && (
                <span title="Pitch">
                  {step.pitch > 0 ? '+' : ''}
                  {step.pitch}
                </span>
              )}
              {step.gate < 1 && <span title="Gate">g{Math.round(step.gate * 100)}</span>}
            </span>
            {step.gain !== 1 && (
              <span
                className="absolute bottom-0 right-0 h-1 rounded-sm"
                style={{ width: `${Math.min(100, step.gain * 50)}%`, background: color }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
